import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export type TicketKind = 'verification' | 'support' | 'abuse' | 'billing';
export type TicketPriority = 'low' | 'normal' | 'high' | 'urgent';
export type TicketStatus = 'open' | 'in_progress' | 'waiting' | 'resolved' | 'closed';

export interface AdminTicket {
  id: string;
  companyId: string | null;
  kind: TicketKind;
  priority: TicketPriority;
  status: TicketStatus;
  subject: string;
  body: string | null;
  assignedTo: string | null;
  openedBy: string | null;
  resolvedAt: string | null;
  resolution: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PendingVerification {
  id: string;
  name: string;
  verificationStatus: string;
  createdAt: string;
}

export function useAdminTickets(filter: { status?: TicketStatus; kind?: TicketKind }) {
  const qs = new URLSearchParams();
  if (filter.status) qs.set('status', filter.status);
  if (filter.kind) qs.set('kind', filter.kind);
  return useQuery({
    queryKey: ['admin-tickets', filter],
    queryFn: async () => {
      const r = await api.get<{ tickets: AdminTicket[] }>(
        `/api/admin/tickets${qs.toString() ? `?${qs}` : ''}`,
      );
      return r.data.tickets;
    },
  });
}

export function useTicketStats() {
  return useQuery({
    queryKey: ['admin-ticket-stats'],
    queryFn: async () => {
      const r = await api.get<Record<TicketStatus, number>>('/api/admin/tickets/stats');
      return r.data;
    },
  });
}

export function usePendingVerifications() {
  return useQuery({
    queryKey: ['admin-verifications'],
    queryFn: async () => {
      const r = await api.get<{ companies: PendingVerification[] }>('/api/admin/verifications/pending');
      return r.data.companies;
    },
  });
}

export function useDecideVerification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { companyId: string; status: 'verified' | 'pending_review' | 'rejected' }) => {
      await api.post(`/api/admin/verifications/${args.companyId}/decision`, { status: args.status });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-verifications'] }),
  });
}

export function useUpdateTicketStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; status: TicketStatus; resolution?: string }) => {
      const r = await api.post<AdminTicket>(`/api/admin/tickets/${args.id}/status`, {
        status: args.status, resolution: args.resolution,
      });
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-tickets'] });
      qc.invalidateQueries({ queryKey: ['admin-ticket-stats'] });
    },
  });
}

/* ── Dashboard ────────────────────────────────────────── */

export interface AdminDashboard {
  totalUsers: number;
  staffUsers: number;
  verifiedUsers: number;
  totalCompanies: number;
  verifiedCompanies: number;
  pendingVerifications: number;
  openTickets: number;
  signupsLast7d: number;
}

export function useAdminDashboard() {
  return useQuery({
    queryKey: ['admin-dashboard'],
    queryFn: async () => {
      const r = await api.get<AdminDashboard>('/api/admin/dashboard');
      return r.data;
    },
  });
}

/* ── Users ────────────────────────────────────────────── */

export type UserRole = 'owner' | 'admin' | 'manager' | 'member' | 'employee';

export interface AdminUser {
  id: string;
  email: string;
  displayName: string;
  role: UserRole;
  companyId: string | null;
  companyName: string | null;
  isStaff: boolean;
  emailVerified: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export function useAdminUsers(search: string) {
  return useQuery({
    queryKey: ['admin-users', search],
    queryFn: async () => {
      const r = await api.get<{ users: AdminUser[] }>(
        `/api/admin/users${search ? `?search=${encodeURIComponent(search)}` : ''}`,
      );
      return r.data.users;
    },
  });
}

export function useSetUserStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; isStaff: boolean }) => {
      await api.post(`/api/admin/users/${args.id}/staff`, { isStaff: args.isStaff });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  });
}

export function useSetUserRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; role: UserRole }) => {
      await api.post(`/api/admin/users/${args.id}/role`, { role: args.role });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  });
}

/* ── Companies ────────────────────────────────────────── */

export interface AdminCompany {
  id: string;
  name: string;
  hqCountryCode: string;
  plan: 'free' | 'growth' | 'global' | 'enterprise';
  verificationStatus: 'pending' | 'verified' | 'pending_review' | 'rejected';
  userCount: number;
  createdAt: string;
}

export function useAdminCompanies(search: string) {
  return useQuery({
    queryKey: ['admin-companies', search],
    queryFn: async () => {
      const r = await api.get<{ companies: AdminCompany[] }>(
        `/api/admin/companies${search ? `?search=${encodeURIComponent(search)}` : ''}`,
      );
      return r.data.companies;
    },
  });
}
